import { FC, PropsWithChildren, useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { WorkerCommentEntry } from '@scale/shared/types/chat';
import { getCommentIsOverallFeedback } from '@scale/shared/chat/response/feedback';
import { Divider, Tag } from '@scale/scaleui';
import { isNil } from 'lodash';
import { SENIOR_LABEL_REVIEW_LEVELS } from '@scale/shared/chat/response';
import { useWorkerCommentsStore } from 'react-scale/components/scalers/tasks/ThreadedInteraction/hooks/useWorkerCommentsStore';

const CHARACTER_LIMIT = 200;

function doScroll(element: Element) {
  element.scrollIntoView({ behavior: 'smooth', block: 'center' });
}

export const useScrollToFeedbackCard = () => {
  const scrollToStep = useCallback((id: any) => {
    const element = document.getElementById(id);
    if (element) {
      doScroll(element);
      return;
    }
  }, []);

  return scrollToStep;
};

type SidebarFeedbackCardProps = PropsWithChildren<{
  id: string;
  workerComment: WorkerCommentEntry;
  isSelected: boolean;
  onClick?: (id: string) => void;
}>;

export const SidebarFeedbackCard: FC<SidebarFeedbackCardProps> = ({
  id,
  workerComment,
  isSelected,
  onClick,
}) => {
  const { comment, author, reviewLevel, turn, title } = workerComment;
  const isOverallFeedback = getCommentIsOverallFeedback(workerComment);

  // Only show the first 200 characters of the comment if it exceeds the limit and add a show more button
  const [showMoreText, setShowMoreText] = useState<boolean>(false);
  const commentExceedsCharacterLimit = comment.length > CHARACTER_LIMIT;
  const commentTextToShow =
    showMoreText || !commentExceedsCharacterLimit
      ? comment
      : `${comment.slice(0, CHARACTER_LIMIT).trim()}...`;

  const handleClick = () => {
    onClick?.(id);
  };

  // We use the last 6 chars to identify the author
  const authorTag = author?.slice(-6);
  const authorLabel = authorTag && `Reviewer (${authorTag})`;

  let isSeniorReview;
  if (!isNil(reviewLevel)) {
    isSeniorReview = SENIOR_LABEL_REVIEW_LEVELS.includes(reviewLevel);
  }
  const selectWorkerComment = useWorkerCommentsStore(state => state.selectWorkerComment);

  useEffect(() => {
    function handleClickOutside(event: any) {
      const panelReference = document.getElementById(id);
      if (
        panelReference &&
        !panelReference.contains(event.target) &&
        !panelReference.outerHTML.includes(event.target.outerHTML)
      ) {
        selectWorkerComment(null);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectWorkerComment, id]);

  return (
    <div
      className={classnames(
        'relative flex flex-col bg-neutral-0 rounded-lg border transition mb-4',
        {
          'hover:cursor-pointer hover:bg-neutral-0/[.40]': !isOverallFeedback,
          'border-primary-300 -ml-2 mr-2 shadow-md border-1 ': isSelected,
          'border-neutral-300': !isSelected,
        },
      )}
      id={id}
      onClick={handleClick}
    >
      <div className="flex flex-grow-0 p-3 justify-between items-center">
        {authorLabel && (
          <Tag size="sm" variant="neutral" icon="user">
            {authorLabel}
          </Tag>
        )}
        {isSeniorReview && (
          <Tag size="sm" variant="primary" icon="shield">
            {'Senior Review'}
          </Tag>
        )}
      </div>
      <div className="flex flex-grow-0 px-3 pb-3 justify-between items-center">
        <div className="flex items-center justify-start gap-2">
          {isOverallFeedback && (
            <Tag size="sm" variant="neutral" icon="earth-americas">
              {!isNil(turn) && turn >= 0 ? `Turn ${turn + 1} Overall` : 'Overall'}
            </Tag>
          )}
          {!isOverallFeedback && !isNil(turn) && turn >= 0 && (
            <Tag size="sm" variant="neutral" icon="highlighter-line">
              Turn {turn + 1}
            </Tag>
          )}
        </div>
      </div>
      {!!title && (
        <div className="px-3 pb-3 text-neutral-700 text-sm font-semibold break-words">{title}</div>
      )}
      <div className="px-3">
        <Divider direction="horizontal" className="mb-3" />
      </div>
      <div className="px-3 pb-3 text-neutral-700 font-normal text-sm break-words">
        <div className="flex flex-col gap-1 whitespace-pre-wrap">
          {commentTextToShow}
          {commentExceedsCharacterLimit && (
            <div
              className="text-primary-400 text-xs"
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                setShowMoreText(!showMoreText);
              }}
            >
              {showMoreText ? 'Show less' : 'Show more'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
